import {
  useState,
  useEffect,
  useRef,
  useCallback,
  Dispatch,
  SetStateAction,
} from "react";
import { Flex, Spinner, Text, Box } from "@chakra-ui/react";
import LoadingTable from "./LoadingTable";
import Table from "./Table";

const PAGE_SIZE = 20;

const TableNoWithPagination = ({
  columns,
  nestedColumns,
  tags,
  get,
  emptyMessage,
  financial = false,
  isProcess = false,
  onCellEdit,
  sortField: externalSortField,
  setSortField: externalSetSortField,
  sortDirection: externalSortDirection,
  setSortDirection: externalSetSortDirection,
  reorderable = false,
  onColumnReorder,
}: {
  columns: {
    label: string;
    index?: string;
    data: any;
    width?: string[] | string | number;
    sortable?: boolean;
    fixed?: boolean;
  }[];
  nestedColumns?: {
    label: string;
    index?: string;
    data: any;
    width?: string[] | string | number;
  }[];
  financial?: boolean;
  isProcess?: boolean;
  tags: any;
  get: (
    pageSize?: number,
    pageIndex?: number,
    sortField?: string,
    sortDirection?: string,
  ) => Promise<any>;
  emptyMessage: string;
  onCellEdit?: (rowId: any, index: string, newValue: any) => Promise<void>;
  sortField?: string;
  setSortField?: Dispatch<SetStateAction<string | undefined>>;
  sortDirection?: "asc" | "desc";
  setSortDirection?: Dispatch<SetStateAction<"asc" | "desc">>;
  reorderable?: boolean;
  onColumnReorder?: (fromLabel: string, toLabel: string) => void;
}) => {
  const [page, setPage] = useState<any[]>([]);
  const [pageIndex, setPageIndex] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [initialLoading, setInitialLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [sortField, setSortField] = useState<string | undefined>(
    externalSortField,
  );
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">(
    externalSortDirection ?? "asc",
  );

  // Vertical scroll container (holds the table)
  const verticalScrollRef = useRef<HTMLDivElement | null>(null);
  // Horizontal scroll container (synced with the table)
  const horizontalScrollRef = useRef<HTMLDivElement | null>(null);
  // Inner div inside horizontal scroll bar — mirrors table width
  const horizontalInnerRef = useRef<HTMLDivElement | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const isFetchingRef = useRef(false);
  const requestSeqRef = useRef(0);
  const tableRef = useRef<HTMLDivElement | null>(null);

  // Sync horizontal scrollbar ↔ table scroll
  useEffect(() => {
    const tableEl = verticalScrollRef.current;
    const hScrollEl = horizontalScrollRef.current;
    const hInnerEl = horizontalInnerRef.current;
    if (!tableEl || !hScrollEl || !hInnerEl) return;

    // Set inner width to match scrollable table width
    const syncWidth = () => {
      hInnerEl.style.width = `${tableEl.scrollWidth}px`;
    };
    syncWidth();

    const onTableScroll = () => {
      hScrollEl.scrollLeft = tableEl.scrollLeft;
    };
    const onHScroll = () => {
      tableEl.scrollLeft = hScrollEl.scrollLeft;
    };

    tableEl.addEventListener("scroll", onTableScroll);
    hScrollEl.addEventListener("scroll", onHScroll);

    const resizeObserver = new ResizeObserver(syncWidth);
    resizeObserver.observe(tableEl);

    return () => {
      tableEl.removeEventListener("scroll", onTableScroll);
      hScrollEl.removeEventListener("scroll", onHScroll);
      resizeObserver.disconnect();
    };
  }, [initialLoading]);

  const fetchPage = useCallback(
    async (
      index: number,
      field?: string,
      direction?: "asc" | "desc",
      reset = false,
    ) => {
      const seq = ++requestSeqRef.current;
      isFetchingRef.current = true;

      if (index === 0) setInitialLoading(true);
      else setLoadingMore(true);

      try {
        const response = await get(
          PAGE_SIZE,
          index,
          field ?? sortField,
          direction ?? sortDirection,
        );
        if (seq !== requestSeqRef.current) return;
        const data: any[] = response.data ?? response;
        const total: number = response.totalCount ?? data.length;

        setPage((prev) => (reset || index === 0 ? data : [...prev, ...data]));
        setPageIndex(index + 1);
        setHasMore((index + 1) * PAGE_SIZE < total);
      } finally {
        if (seq === requestSeqRef.current) {
          isFetchingRef.current = false;
          setInitialLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [get, sortField, sortDirection],
  );

  // Initial load on tags or filter change (get's identity changes when the caller's filters do)
  useEffect(() => {
    setPage([]);
    setPageIndex(0);
    setHasMore(true);
    fetchPage(0, sortField, sortDirection, true);
  }, [tags, get]);

  // Infinite scroll — observer root is the vertical scroll container
  useEffect(() => {
    const scrollEl = verticalScrollRef.current;
    const sentinelEl = sentinelRef.current;
    if (!scrollEl || !sentinelEl) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0].isIntersecting &&
          hasMore &&
          !loadingMore &&
          !initialLoading &&
          !isFetchingRef.current
        ) {
          fetchPage(pageIndex);
        }
      },
      {
        root: scrollEl,
        threshold: 0.1,
      },
    );

    observer.observe(sentinelEl);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, initialLoading, pageIndex, fetchPage]);

  const handleSort = (field: string, direction: "asc" | "desc") => {
    setSortField(field);
    setSortDirection(direction);
    externalSetSortField?.(field);
    externalSetSortDirection?.(direction);
    setPage([]);
    setPageIndex(0);
    setHasMore(true);
    fetchPage(0, field, direction, true);
  };

  if (initialLoading) return <LoadingTable />;

  return (
    <Flex flexDirection="column" width="100%" height="85vh">
      <Box
        ref={verticalScrollRef}
        overflowX="hidden"
        overflowY="scroll"
        flex="1"
        sx={{
          overflowY: "scroll",

          // Horizontal escondida
          "&::-webkit-scrollbar:horizontal": {
            height: "0px",
          },

          // Vertical visível
          "&::-webkit-scrollbar": {
            width: "8px",
          },

          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#CBD5E0",
            borderRadius: "4px",
          },

          "&::-webkit-scrollbar-track": {
            backgroundColor: "#EDF2F7",
          },

          // Firefox
          scrollbarWidth: "thin",
          scrollbarColor: "#CBD5E0 #EDF2F7",

          // Sticky header
          "thead th": {
            position: "sticky",
            top: 0,
            zIndex: 2,
            backgroundColor: "white",
            boxShadow: "0 1px 0 0 #e2e8f0",
          },

          "thead th:first-of-type": {
            position: "sticky",
            left: 0,
            zIndex: 3,
            backgroundColor: "white",
          },

          "tbody td:first-of-type": {
            position: "sticky",
            left: 0,
            zIndex: 1,
            backgroundColor: "white",
            boxShadow: "1px 0 0 0 #e2e8f0",
          },

          table: {
            borderCollapse: "separate",
            borderSpacing: 0,
          },
        }}
      >
        <Table
          onCellEdit={onCellEdit}
          isProcess={isProcess}
          sortField={sortField}
          setSortField={setSortField}
          sortDirection={sortDirection}
          setSortDirection={setSortDirection}
          onSort={handleSort}
          reorderable={reorderable}
          onColumnReorder={onColumnReorder}
          columns={columns.map((col) => ({
            ...col,
            onHeaderClick: col.sortable
              ? () =>
                  handleSort(
                    col.index ?? col.label,
                    sortField === (col.index ?? col.label) &&
                      sortDirection === "asc"
                      ? "desc"
                      : "asc",
                  )
              : undefined,
            isSorted: sortField === (col.index ?? col.label),
            isSortedDesc:
              sortField === (col.index ?? col.label) &&
              sortDirection === "desc",
          }))}
          nestedColumns={nestedColumns}
          page={page}
          financial={financial}
          emptyMessage={emptyMessage}
        />

        <div ref={sentinelRef} style={{ height: 1 }} />

        {loadingMore && (
          <Flex justifyContent="center" alignItems="center" py="24px" gap="8px">
            <Spinner size="sm" color="var(--icon-color)" />
            <Text fontSize="sm" color="gray.500">
              Carregando mais...
            </Text>
          </Flex>
        )}

        {!hasMore && page.length > 0 && (
          <Flex justifyContent="center" py="16px">
            <Text fontSize="sm" color="gray.400">
              Todos os registros foram carregados ({page.length} no total)
            </Text>
          </Flex>
        )}
      </Box>

      <Box
        ref={horizontalScrollRef}
        overflowX="auto"
        overflowY="hidden"
        height="12px"
        sx={{
          "&::-webkit-scrollbar": { height: "6px" },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#CBD5E0",
            borderRadius: "3px",
          },
        }}
      >
        <div ref={horizontalInnerRef} style={{ height: 1 }} />
      </Box>
    </Flex>
  );
};

export default TableNoWithPagination;
